import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import IconPdf from "../../../../images/dashboard/icon-pdf.png";
import IconWord from "../../../../images/dashboard/icon-word.png";
import apiConfig from '../../../credentials/apiConfig';

const DownloadModal = ({ show, onHide, clientId, selectedDocument }) => {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fileKey = selectedDocument?.fileKey;
  const fileUrl = selectedDocument?.fileUrl;

  const getFileNameFromUrl = (url) => {
    if (!url) return "DownloadedDocument";
    return decodeURIComponent(url.split('/').pop().split('?')[0]);
  };


  // Reset error when modal is opened/closed
  useEffect(() => {
    if (show) {
      setError("");
    }
  }, [show]);

  // Validate inputs when modal opens
  useEffect(() => {
    if (show) {
      if (!selectedDocument?.fileKey || selectedDocument?.fileKey.trim() === "") {
        setError("No document is selected. Please select a document before downloading.");
      } else if (!clientId || clientId.trim() === "") {
        setError("Client ID is missing. Please ensure client information is available.");
      } else {
        setError("");
      }
    }
  }, [show, selectedDocument?.fileKey, clientId]);

  const handleDownload = async () => {
    // Clear previous errors
    setError("");

    // Additional validation
    if (!selectedFormat) {
      setError("Please select a download format.");
      return;
    }

    if (!clientId || !selectedDocument?.fileKey) {
      setError("Missing required information. Please select a document before downloading.");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(`${apiConfig.apiBaseURL}/downloadSummary`, {
        clientId,
        fileKey,
        format: selectedFormat
      }, {
        headers: {
          'x-api-key': apiConfig['x-api-key'],
          'Content-Type': 'application/json',
        },
        transformResponse: [(data) => data] // prevent JSON parsing, keep raw base64
      });

      const base64 = response.data;

      if (!base64 || base64.length < 50 || base64.startsWith("{")) {
        console.error("Invalid base64 response:", base64.slice(0, 100));
        throw new Error("Server did not return a valid file.");
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: selectedFormat === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });

      const originalName = getFileNameFromUrl(fileUrl);
      const fileExtension = selectedFormat === "pdf" ? ".pdf" : ".docx";

      // Remove leading numeric prefix and extension, add suffix
      const cleanedName = originalName.replace(/^\d+-/, '').replace(/\.[^/.]+$/, '');
      const fileName = `Summary of ${cleanedName} ${fileExtension}`;

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Download started.");
      onHide();
    } catch (error) {
      console.error("Download failed:", error);
      setError(error.message || "Failed to download the document. Please try again.");
      toast.error("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className='fw-bold'>Download</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {!clientId || !selectedDocument?.fileKey ? (
          <div className="text-center py-3">
            <p>Please select a document before downloading.</p>
          </div>
        ) : (
          <>
            <Row className="mb-4">
              <Col>
                <Button
                  variant={selectedFormat === 'pdf' ? "primary" : "outline-primary"}
                  className="w-100 py-2"
                  onClick={() => setSelectedFormat('pdf')}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2 py-2">
                    <img src={IconPdf} width={30} height={30} alt="PDF Icon" />
                    <span>PDF Format</span>
                  </div>
                </Button>
              </Col>
              <Col>
                <Button
                  variant={selectedFormat === 'word' ? "primary" : "outline-primary"}
                  className="w-100 py-2"
                  onClick={() => setSelectedFormat('word')}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2 py-2">
                    <img src={IconWord} width={30} height={30} alt="Word Icon" />
                    <span>Word Format</span>
                  </div>
                </Button>
              </Col>
            </Row>
          </>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button variant="outline-primary" onClick={onHide} disabled={loading}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={!selectedFormat || loading || !clientId || !selectedDocument?.fileKey}
          >
            {loading ? "Downloading..." : "Download"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DownloadModal;